import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, TrendingDown, DollarSign, Wallet, ArrowRight, Activity,
  Crown, BookOpen, Calendar, ShoppingBag, BarChart3, Target,
  CheckCircle, Clock, Award, GraduationCap, Copy, Coins, Building2,
  User, Mail, Shield, Lock, Unlock, FileText, Users
} from 'lucide-react';

import { API } from '@/config/api';

const quickActions = [
  { title: 'Trading Terminal', description: 'Trade stocks, crypto, forex', icon: TrendingUp, link: '/trading', color: 'bg-blue-500/20 text-blue-400' },
  { title: 'Copy Trading', description: 'Follow top traders', icon: Copy, link: '/copy-trading', color: 'bg-purple-500/20 text-purple-400' },
  { title: 'Trade Journal', description: 'Track your trades', icon: BookOpen, link: '/trade-journal', color: 'bg-emerald-500/20 text-emerald-400' },
  { title: 'Staking', description: 'Earn passive income', icon: Coins, link: '/staking', color: 'bg-yellow-500/20 text-yellow-400' },
  { title: 'Real Estate', description: 'REIT investments', icon: Building2, link: '/real-estate', color: 'bg-orange-500/20 text-orange-400' },
  { title: 'Security Settings', description: 'Manage 2FA & security', icon: Shield, link: '/security', color: 'bg-red-500/20 text-red-400' },
];

// Gated content based on NEW membership tier structure
// Tier 1 (Legacy Explorer/Free): Paper trading, Gilded Study basics, Portfolio, Trading Journal, Lead Generator, AI Assistant, Free Services
// Tier 2 (Wealth Architect): Everything in T1 + NRL University regular courses
// Tier 3 (Dynasty Builder): Everything in T1+T2 + Live trading + 1-on-1 biweekly + Full courses
const gatedContent = [
  { title: 'Paper Trading', description: 'Practice trading risk-free', icon: TrendingUp, link: '/trading', requiredTier: 'legacy_explorer', color: 'emerald' },
  { title: 'Gilded Study', description: 'Basic financial education', icon: GraduationCap, link: '/gilded-study', requiredTier: 'legacy_explorer', color: 'blue' },
  { title: 'Portfolio Overview', description: 'Track your investments', icon: BarChart3, link: '/portfolio', requiredTier: 'legacy_explorer', color: 'purple' },
  { title: 'Trading Journal', description: 'Log and analyze trades', icon: BookOpen, link: '/trade-journal', requiredTier: 'legacy_explorer', color: 'orange' },
  { title: 'AI Assistant', description: 'Your wealth-building advisor', icon: Target, link: '/ai-assistant', requiredTier: 'legacy_explorer', color: 'cyan' },
  { title: 'NRL University', description: 'Regular courses', icon: GraduationCap, link: 'https://nrluniversity.com', requiredTier: 'wealth_architect', color: 'yellow', external: true },
  { title: 'Live Trading', description: 'Real money trading', icon: DollarSign, link: '/trading', requiredTier: 'dynasty_builder', color: 'gold' },
  { title: '1-on-1 Coaching', description: 'Biweekly sessions', icon: User, link: '/booking', requiredTier: 'dynasty_builder', color: 'rose' },
  { title: 'Full Course Access', description: 'At-your-pace learning', icon: Award, link: 'https://nrluniversity.com', requiredTier: 'dynasty_builder', color: 'amber', external: true },
];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [cryptoData, setCryptoData] = useState([]);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const fetchData = useCallback(async () => {
    try {
      const [dashboardRes, cryptoRes, membershipRes] = await Promise.all([
        fetch(`${API}/user/dashboard`, { credentials: 'include' }),
        fetch(`${API}/market/crypto`),
        fetch(`${API}/membership/status`, { credentials: 'include' })
      ]);
      
      if (dashboardRes.ok) {
        setDashboardData(await dashboardRes.json());
      }
      
      if (cryptoRes.ok) {
        const data = await cryptoRes.json();
        setCryptoData(Array.isArray(data) ? data.slice(0, 5) : []);
      }
      
      if (membershipRes.ok) {
        setMembership(await membershipRes.json());
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };
  
  const formatPercent = (value) => {
    const num = parseFloat(value);
    return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
  };
  
  const getMembershipColor = (tier) => {
    switch(tier) {
      case 'dynasty_builder': return 'bg-[#D4AF37] text-black';
      case 'wealth_architect': return 'bg-blue-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getMembershipLevel = (tier) => {
    if (tier === 'dynasty_builder') return 3;
    if (tier === 'wealth_architect') return 2;
    return 1; // legacy_explorer
  };

  const getTierDisplayName = (tier) => {
    switch(tier) {
      case 'dynasty_builder': return 'Dynasty Builder';
      case 'wealth_architect': return 'Wealth Architect';
      default: return 'Legacy Explorer';
    }
  };

  const hasAccessToContent = (requiredTier, userTier) => {
    const tierLevels = {
      'legacy_explorer': 1,
      'free': 1,
      'wealth_architect': 2,
      'basic': 2,
      'dynasty_builder': 3,
      'premium': 3
    };
    return (tierLevels[userTier] || 1) >= (tierLevels[requiredTier] || 1);
  };

  // Helper to check content access (uses the function above with current membership)
  const checkContentAccess = (requiredTier) => {
    const userTier = membership?.membership_tier || membership?.membership || 'legacy_explorer';
    return hasAccessToContent(requiredTier, userTier);
  };

  const getTierName = (tier) => {
    switch(tier) {
      case 'dynasty_builder':
      case 'premium': return 'Dynasty Builder';
      case 'wealth_architect':
      case 'basic': return 'Wealth Architect';
      default: return 'Legacy Explorer';
    }
  };
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-20 pb-12 px-4 sm:px-6 lg:px-8" data-testid="dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Account Details Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* User Avatar */}
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="h-8 w-8 text-black" />
                )}
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white">
                  Welcome back, <span className="text-[#D4AF37]">{user?.name?.split(' ')[0] || 'Legacy Builder'}</span>
                </h1>
                <p className="text-gray-400 flex items-center gap-2">
                  <Mail className="h-4 w-4" /> {user?.email || 'user@example.com'}
                </p>
              </div>
            </div>
            
            {/* Membership Badge */}
            <div className="flex flex-col items-end gap-2">
              <Badge className={`${getMembershipColor(membership?.membership)} px-4 py-2 text-sm`}>
                <Crown className="h-4 w-4 mr-2" />
                {getTierName(membership?.membership)}
              </Badge>
              {membership?.membership !== 'premium' && (
                <Link to="/#pricing">
                  <Button variant="outline" size="sm" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black">
                    <Shield className="h-4 w-4 mr-2" /> Upgrade Plan
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Account Summary Card */}
        <Card className="bg-[#14120f] border-[#1c1814] mb-8">
          <CardHeader className="border-b border-[#1c1814]">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <User className="h-5 w-5 text-[#D4AF37]" />
              Account Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Full Name</p>
                <p className="text-white font-medium">{user?.name || 'Not set'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Email</p>
                <p className="text-white font-medium truncate">{user?.email || 'Not set'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Membership</p>
                <Link to="/subscription" className="text-[#D4AF37] font-medium hover:underline">
                  {getTierName(membership?.membership)}
                </Link>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Member Since</p>
                <p className="text-white font-medium">{dashboardData?.user?.created_at ? new Date(dashboardData.user.created_at).toLocaleDateString() : 'Today'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#14120f] border-[#1c1814]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Wallet Balance</p>
                  <p className="text-2xl font-bold text-[#D4AF37]">
                    {formatCurrency(dashboardData?.wallet_balance)}
                  </p>
                </div>
                <Wallet className="h-8 w-8 text-[#D4AF37]" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#14120f] border-[#1c1814]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Trades</p>
                  <p className="text-2xl font-bold text-white">
                    {dashboardData?.stats?.total_trades || 0}
                  </p>
                </div>
                <BarChart3 className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#14120f] border-[#1c1814]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Orders</p>
                  <p className="text-2xl font-bold text-white">
                    {dashboardData?.stats?.total_orders || 0}
                  </p>
                </div>
                <ShoppingBag className="h-8 w-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#14120f] border-[#1c1814]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Courses</p>
                  <p className="text-2xl font-bold text-white">
                    {dashboardData?.stats?.courses_completed || 0}
                  </p>
                </div>
                <GraduationCap className="h-8 w-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Portfolio Summary - 2 columns */}
          <Card className="bg-[#14120f] border-[#1c1814] lg:col-span-2">
            <CardHeader className="border-b border-[#1c1814]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-[#D4AF37]" />
                  Portfolio Overview
                </CardTitle>
                <Link to="/trading">
                  <Button size="sm" className="bg-[#D4AF37] text-black">
                    Trade Now <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {/* Main Balance Display */}
              <div className="mb-8">
                <p className="text-sm text-gray-400 mb-2">Total Balance</p>
                <p className="text-4xl font-bold text-[#D4AF37]">
                  {formatCurrency(dashboardData?.portfolio_value || 0)}
                </p>
                <p className="text-sm text-gray-500 mt-1">Paper Trading Account</p>
              </div>
              
              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-6 border-y border-[#1c1814]">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Cash Balance</p>
                  <p className="text-xl font-bold text-white">
                    {formatCurrency(dashboardData?.cash_balance || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Holdings Value</p>
                  <p className="text-xl font-bold text-white">
                    {formatCurrency(dashboardData?.holdings_value || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Total Trades</p>
                  <p className="text-xl font-bold text-white">
                    {dashboardData?.recent_trades?.length || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Status</p>
                  <Badge className="bg-emerald-500/20 text-emerald-400 text-sm">Active</Badge>
                </div>
              </div>
              
              {/* Recent Trades */}
              <div className="mt-6">
                <p className="text-sm text-gray-400 mb-4">Recent Trades</p>
                {dashboardData?.recent_trades?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboardData.recent_trades.slice(0, 3).map((trade, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-[#0a0a0f] rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${trade.type === 'buy' ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                            {trade.type === 'buy' ? 
                              <TrendingUp className="h-5 w-5 text-emerald-400" /> : 
                              <TrendingDown className="h-5 w-5 text-red-400" />
                            }
                          </div>
                          <div>
                            <p className="text-white font-medium">{trade.symbol}</p>
                            <p className="text-sm text-gray-500">{trade.type?.toUpperCase()} • {trade.quantity} shares</p>
                          </div>
                        </div>
                        <p className="text-white font-medium">{formatCurrency(trade.price)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-[#0a0a0f] rounded-lg">
                    <Activity className="h-10 w-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 mb-2">No trades yet</p>
                    <p className="text-sm text-gray-500">Start trading to see your activity here</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Upcoming Bookings */}
          <Card className="bg-[#14120f] border-[#1c1814]">
            <CardHeader className="border-b border-[#1c1814]">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#D4AF37]" />
                Upcoming Sessions
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {dashboardData?.upcoming_bookings?.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.upcoming_bookings.map((booking, i) => (
                    <div key={i} className="p-4 bg-[#0a0a0f] rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="h-4 w-4 text-[#D4AF37]" />
                        <span className="text-white font-medium">{booking.service_name}</span>
                      </div>
                      <p className="text-sm text-gray-400">{booking.date} at {booking.time}</p>
                      <Badge className="mt-2 bg-blue-500/20 text-blue-400">{booking.status}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 mb-4">No upcoming sessions</p>
                  <Link to="/booking">
                    <Button size="sm" className="bg-[#D4AF37] text-black">
                      Book a Session
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Market Overview */}
        <Card className="bg-[#14120f] border-[#1c1814] mb-8">
          <CardHeader className="border-b border-[#1c1814]">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Activity className="h-5 w-5 text-[#D4AF37]" />
                Market Overview
              </CardTitle>
              <Link to="/trading">
                <Button variant="ghost" className="text-[#D4AF37]">
                  View All Markets <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : cryptoData.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {cryptoData.map((coin) => (
                  <div 
                    key={coin.id}
                    className="p-4 bg-[#0a0a0f] rounded-lg border border-[#1c1814] hover:border-[#D4AF37]/30 transition-colors cursor-pointer"
                    onClick={() => navigate('/trading')}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <img src={coin.image} alt={coin.name} className="w-6 h-6" />
                      <span className="text-sm text-white uppercase font-bold">{coin.symbol}</span>
                    </div>
                    <p className="text-lg font-bold text-white">{formatCurrency(coin.current_price)}</p>
                    <p className={`text-sm ${coin.price_change_percentage_24h >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {coin.price_change_percentage_24h >= 0 ? <TrendingUp className="inline h-3 w-3 mr-1" /> : <TrendingDown className="inline h-3 w-3 mr-1" />}
                      {formatPercent(coin.price_change_percentage_24h)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">Unable to load market data</p>
            )}
          </CardContent>
        </Card>
        
        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {quickActions.map((action) => (
              <Link key={action.link} to={action.link}>
                <Card className="bg-[#14120f] border-[#1c1814] hover:border-[#D4AF37]/30 transition-all h-full">
                  <CardContent className="p-4 text-center">
                    <div className={`w-12 h-12 rounded-lg ${action.color} flex items-center justify-center mx-auto mb-3`}>
                      <action.icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-white font-medium text-sm mb-1">{action.title}</h3>
                    <p className="text-xs text-gray-500">{action.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
        
        {/* Recent Orders */}
        {dashboardData?.recent_orders?.length > 0 && (
          <Card className="bg-[#14120f] border-[#1c1814]">
            <CardHeader className="border-b border-[#1c1814]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <ShoppingBag className="h-5 w-5 text-[#D4AF37]" />
                  Recent Orders
                </CardTitle>
                <Link to="/fashion">
                  <Button variant="ghost" className="text-[#D4AF37]">
                    Shop More <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-3">
                {dashboardData.recent_orders.map((order, i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-[#1c1814] last:border-0">
                    <div>
                      <p className="text-white font-medium">Order #{order.order_id?.slice(0, 8)}</p>
                      <p className="text-sm text-gray-500">{order.items?.length || 0} items • {new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#D4AF37] font-bold">{formatCurrency(order.total)}</p>
                      <Badge className={order.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}>
                        {order.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Gated Content Access */}
        <Card className="bg-[#14120f] border-[#1c1814] mt-8">
          <CardHeader className="border-b border-[#1c1814]">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Shield className="h-5 w-5 text-[#D4AF37]" />
                Content Access
              </CardTitle>
              <Badge className="bg-[#D4AF37]/20 text-[#D4AF37]">
                {getTierName(membership?.membership)}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {gatedContent.map((content) => {
                const hasAccess = checkContentAccess(content.requiredTier);
                const colorClasses = {
                  emerald: 'bg-emerald-500/20 text-emerald-400',
                  blue: 'bg-blue-500/20 text-blue-400',
                  purple: 'bg-purple-500/20 text-purple-400',
                  orange: 'bg-orange-500/20 text-orange-400',
                  yellow: 'bg-yellow-500/20 text-yellow-400',
                  pink: 'bg-pink-500/20 text-pink-400',
                  cyan: 'bg-cyan-500/20 text-cyan-400',
                  gold: 'bg-[#D4AF37]/20 text-[#D4AF37]',
                  rose: 'bg-rose-500/20 text-rose-400',
                  amber: 'bg-amber-500/20 text-amber-400',
                };
                
                const handleClick = () => {
                  if (!hasAccess) return;
                  if (content.external) {
                    window.open(content.link, '_blank');
                  } else {
                    navigate(content.link);
                  }
                };
                
                return (
                  <div
                    key={content.title}
                    onClick={handleClick}
                    className={`relative p-4 rounded-lg border transition-all ${
                      hasAccess 
                        ? 'bg-[#0a0a0f] border-[#1c1814] hover:border-[#D4AF37]/50 cursor-pointer' 
                        : 'bg-[#0a0a0f]/50 border-[#1c1814]/50 opacity-60'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${colorClasses[content.color] || colorClasses.emerald} flex items-center justify-center mx-auto mb-2`}>
                      <content.icon className="h-5 w-5" />
                    </div>
                    <h4 className="text-white font-medium text-sm text-center mb-1">{content.title}</h4>
                    <p className="text-xs text-gray-500 text-center">{content.description}</p>
                    
                    {/* Lock/Unlock indicator */}
                    <div className="absolute top-2 right-2">
                      {hasAccess ? (
                        <Unlock className="h-4 w-4 text-emerald-400" />
                      ) : (
                        <Lock className="h-4 w-4 text-gray-500" />
                      )}
                    </div>
                    
                    {/* Required tier badge */}
                    {!hasAccess && (
                      <div className="mt-2 text-center">
                        <Badge className="bg-gray-500/20 text-gray-400 text-xs">
                          {content.requiredTier === 'basic' ? 'Wealth Architect' : 'Dynasty Builder'}
                        </Badge>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {membership?.membership_tier !== 'dynasty_builder' && (
              <div className="mt-6 p-4 bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Unlock All Content</p>
                    <p className="text-gray-400 text-sm">
                      {membership?.membership_tier === 'wealth_architect' 
                        ? 'Upgrade to Dynasty Builder for live trading & 1-on-1 coaching'
                        : 'Upgrade for NRL University courses and more'
                      }
                    </p>
                  </div>
                  <Button 
                    className="bg-[#D4AF37] text-black hover:bg-[#B8962E]"
                    onClick={async () => {
                      const packageId = membership?.membership_tier === 'wealth_architect' 
                        ? 'dynasty_builder_monthly' 
                        : 'wealth_architect_monthly';
                      
                      try {
                        const response = await fetch(`${API}/payments/create-checkout`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify({
                            package_id: packageId,
                            package_type: 'membership',
                            origin_url: window.location.origin
                          })
                        });
                        
                        const data = await response.json();
                        
                        if (data.success && data.checkout_url) {
                          window.location.href = data.checkout_url;
                        } else {
                          alert(data.detail || 'Failed to start checkout');
                        }
                      } catch (error) {
                        console.error('Checkout error:', error);
                        alert('Failed to start checkout. Please try again.');
                      }
                    }}
                  >
                    Upgrade Now <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bookings History */}
        <Card className="bg-[#14120f] border-[#1c1814] mt-8">
          <CardHeader className="border-b border-[#1c1814]">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#D4AF37]" />
                Your Bookings
              </CardTitle>
              <Link to="/booking">
                <Button variant="ghost" className="text-[#D4AF37]">
                  Book Consultation <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {dashboardData?.upcoming_bookings?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.upcoming_bookings.map((booking, i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-[#1c1814] last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{booking.service_name}</p>
                        <p className="text-sm text-gray-500">{booking.date} at {booking.time}</p>
                      </div>
                    </div>
                    <Badge className={
                      booking.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400' :
                      booking.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-blue-500/20 text-blue-400'
                    }>
                      {booking.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 mb-4">No bookings yet</p>
                <Link to="/booking">
                  <Button className="bg-[#D4AF37] text-black hover:bg-[#B8962E]">
                    Book a Consultation
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
